/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { DAILY_PROMPT_ANCHOR_PROGRAM_ID } from '@/utils/dailyJournalPrompts';
import { useDiscoveryProfileSignals } from '@/hooks/useDiscoveryProfileSignals';
import { Program } from '@/types';
import type { DiscoveryProfileSignals } from '@/utils/programDiscoveryEligibility';
import { filterProgramsForDiscovery } from '@/utils/programDiscoveryEligibility';
import { isOfficialCatalogProgram } from '@/utils/programCatalogDiscovery';
import { programKeys } from '@/lib/queryKeys';

/**
 * User enrollment in a program
 */
interface UserEnrollment {
  id: string;
  program_id: string;
  user_id: string;
  status: 'active' | 'in_progress' | 'completed' | 'paused';
  progress_percent: number;
  current_prompt_index: number;
  started_at: string;
  completed_at: string | null;
  program: Program;
}

const PROGRAM_LIST_FIELDS = `
  id,
  title,
  description,
  thumbnail_url,
  hero_thumbnail_url,
  access_tier,
  program_type,
  prompt_count,
  total_duration,
  is_published,
  visibility,
  program_scope,
  deleted_at,
  created_at,
  created_by,
  creation_source,
  mental_health_approved,
  discovery_rule_type,
  discovery_window_start,
  discovery_window_end,
  discovery_zodiac_sign
`;

const PROGRAM_LIST_FIELDS_LEGACY = `
  id,
  title,
  description,
  thumbnail_url,
  access_tier,
  program_type,
  prompt_count,
  total_duration,
  is_published,
  visibility,
  deleted_at,
  created_at,
  created_by,
  creation_source,
  mental_health_approved,
  discovery_rule_type,
  discovery_window_start,
  discovery_window_end,
  discovery_zodiac_sign
`;

const PROGRAM_LIST_SELECT = `
  ${PROGRAM_LIST_FIELDS},
  program_tags(
    tags(id, name, color, category)
  )
`;

const PROGRAM_LIST_SELECT_LEGACY = `
  ${PROGRAM_LIST_FIELDS_LEGACY},
  program_tags(
    tags(id, name, color, category)
  )
`;

function isMissingHeroThumbnailColumn(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as { code?: string; message?: string };
  return maybeError.code === '42703' && (maybeError.message || '').includes('hero_thumbnail_url');
}

export async function executeProgramsSelect<T>(
  runQuery: (selectClause: string) => any
): Promise<{ data: T[] | null; error: any }> {
  const result = await runQuery(PROGRAM_LIST_SELECT) as { data: T[] | null; error: any };
  if (!isMissingHeroThumbnailColumn(result.error)) {
    return result;
  }

  console.warn('programs.hero_thumbnail_url is missing in this environment, retrying with legacy select');
  return await runQuery(PROGRAM_LIST_SELECT_LEGACY) as { data: T[] | null; error: any };
}

function mapProgramTags(program: any) {
  return (program.program_tags as any[])
    ?.map((pt) => pt.tags || pt.tag)
    .filter(Boolean) || [];
}


async function attachProgramTags<T extends { id?: string | null; program_tags?: any[] | null }>(
  programs: T[]
): Promise<T[]> {
  if (!programs.length) return programs;

  const missingTags = programs.some((program) => !Array.isArray(program.program_tags));
  if (!missingTags) return programs;

  const programIds = Array.from(new Set(programs.map((program) => program.id).filter(Boolean))) as string[];
  if (programIds.length === 0) return programs;

  const { data, error } = await supabase
    .from('program_tags')
    .select('program_id, tags(id, name, color, category)')
    .in('program_id', programIds);

  if (error) {
    console.warn('Unable to hydrate program tags for discovery queries:', error.message);
    return programs;
  }

  const tagsByProgramId = new Map<string, any[]>();
  (data || []).forEach((row: any) => {
    if (!row?.program_id || !row?.tags) return;
    const existing = tagsByProgramId.get(row.program_id) || [];
    existing.push({ tags: row.tags });
    tagsByProgramId.set(row.program_id, existing);
  });

  return programs.map((program) => {
    if (Array.isArray(program.program_tags)) return program;
    return {
      ...program,
      program_tags: tagsByProgramId.get(program.id as string) || [],
    };
  });
}

async function attachProgramCreators<T extends { created_by?: string | null }>(
  programs: T[]
): Promise<Array<T & { creator: { id: string; display_name: string | null; avatar_url: string | null; is_admin: boolean | null; slug: string | null; party_animal: string | null } | null }>> {
  if (!programs.length) return [] as Array<T & { creator: null }>;

  const creatorIds = [...new Set(programs.map((program) => program.created_by).filter(Boolean))] as string[];
  if (creatorIds.length === 0) {
    return programs.map((program) => ({ ...program, creator: null }));
  }

  const [{ data: profilesByProfileId }, { data: profilesByUserId }] = await Promise.all([
    supabase
      .from('member_profiles')
      .select('id, user_id, display_name, avatar_url, is_admin, slug, party_animal')
      .in('id', creatorIds),
    supabase
      .from('member_profiles')
      .select('id, user_id, display_name, avatar_url, is_admin, slug, party_animal')
      .in('user_id', creatorIds),
  ]);

  const creatorLookup = new Map<
    string,
    { id: string; display_name: string | null; avatar_url: string | null; is_admin: boolean | null; slug: string | null; party_animal: string | null }
  >();

  [...(profilesByProfileId || []), ...(profilesByUserId || [])].forEach((profile) => {
    const normalized = {
      id: profile.user_id,
      display_name: profile.display_name ?? null,
      avatar_url: profile.avatar_url ?? null,
      is_admin: profile.is_admin ?? null,
      slug: profile.slug ?? null,
      party_animal: profile.party_animal ?? null,
    };
    creatorLookup.set(profile.id, normalized);
    creatorLookup.set(profile.user_id, normalized);
  });

  return programs.map((program) => ({
    ...program,
    creator: program.created_by ? (creatorLookup.get(program.created_by) ?? null) : null,
  }));
}

export async function normalizePrograms(programs: any[]): Promise<Program[]> {
  const withTags = await attachProgramTags(programs);
  const withCreators = await attachProgramCreators(withTags);
  return withCreators.map((program) => ({
    ...program,
    tags: mapProgramTags(program),
  })) as Program[];
}

/** RPC omits deleted_at and publication metadata; validate against current catalog rows. */
export async function validateOfficialRpcPrograms(programs: any[]): Promise<any[] | null> {
  if (!programs.length) return [];
  const { data, error } = await supabase.from('programs')
    .select('id, slug, creation_source, is_published, deleted_at, visibility, program_scope, program_type')
    .in('id', programs.map(program => program.id));
  if (error) return null;
  const metadata = new Map((data || []).filter(program => isOfficialCatalogProgram(program))
    .map(program => [program.id, program]));
  return programs.filter(program => metadata.has(program.id))
    .map(program => ({ ...program, ...metadata.get(program.id) }));
}

const DISCOVERY_CANDIDATE_MULTIPLIER = 4;
const MIN_DISCOVERY_CANDIDATES = 24;

export function getDiscoveryCandidateLimit(limit: number): number {
  return Math.max(limit * DISCOVERY_CANDIDATE_MULTIPLIER, MIN_DISCOVERY_CANDIDATES);
}

/**
 * Fetch all published PowerPrompt programs (excludes course-style programs)
 * Uses fast RPC function to bypass slow RLS verify_premium_access() calls
 */
export async function fetchPrograms(): Promise<Program[]> {
  // Try fast RPC first (bypasses slow RLS)
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('get_published_programs');

  if (!rpcError && rpcData) {
    // RPC succeeded - return programs (no tags from RPC)
    const validated = await validateOfficialRpcPrograms(rpcData as any[]);
    if (validated !== null) return normalizePrograms(validated);
  }

  // Fallback to regular query if RPC not available
  console.warn('RPC get_published_programs not available, using slow query');
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
  );

  if (error) throw error;

  return normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));
}

/**
 * Fetch published PowerPrompt programs that are explicitly free.
 * This powers the public /free route and the logged-out free shelf, so admin
 * can control the shelf by changing a program's access tier.
 */
async function fetchFreePrograms(): Promise<Program[]> {
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('access_tier', 'free')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
  );

  if (error) throw error;

  return normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));
}

function isMissingFreeSampleColumn(error: unknown): boolean {
  const candidate = error as { code?: string; message?: string } | null;
  return candidate?.code === '42703' && String(candidate.message || '').includes('free_sample');
}

/**
 * The /free page lists only the admin-picked samples (programs.free_sample),
 * the free programs every signed-in member can start once the core access
 * gate is active. Before the column exists in an environment, it falls back
 * to the full free shelf so the page never goes empty.
 */
export async function fetchFreeSamplePrograms(): Promise<Program[]> {
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(`${programSelect}, free_sample`)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('access_tier', 'free')
      .eq('free_sample', true)
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
  );

  if (isMissingFreeSampleColumn(error)) {
    console.warn('programs.free_sample is missing in this environment, listing every free program');
    return fetchFreePrograms();
  }
  if (error) throw error;

  return normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));
}

/**
 * Fetch newest published PowerPrompt programs
 * Shows both free and premium programs, ordered by creation date
 */
export async function fetchNewestPrograms(limit = 6, discoverySignals?: DiscoveryProfileSignals): Promise<Program[]> {
  // Try fast RPC first (bypasses slow/blocked RLS)
  const { data: rpcData, error: rpcError } = await supabase.rpc('get_published_programs');
  const validatedRpc = !rpcError && rpcData ? await validateOfficialRpcPrograms(rpcData as any[]) : null;
  if (validatedRpc !== null) {
    const candidatePrograms = validatedRpc
      .filter(program => isOfficialCatalogProgram(program, true))
      .filter(p => {
        if (p?.program_type) {
          return String(p.program_type).toLowerCase() === 'powerprompt';
        }
        return true;
      })
      .filter(p => p?.is_published ?? true)
      .sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return bTime - aTime;
      });

    const normalized = await normalizePrograms(candidatePrograms);
    return filterProgramsForDiscovery(normalized, discoverySignals).slice(0, limit);
  }

  // Fallback to direct query if RPC unavailable
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(getDiscoveryCandidateLimit(limit))
  );

  if (error) throw error;

  const normalized = await normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));
  return filterProgramsForDiscovery(normalized, discoverySignals).slice(0, limit);
}

/**
 * Fetch user's enrolled programs with progress
 */
async function fetchUserEnrollments(userId: string): Promise<UserEnrollment[]> {
  const { data, error } = await supabase
    .from('user_sessions')
    .select(`
      id,
      program_id,
      user_id,
      status,
      current_prompt_index,
      started_at,
      completed_at,
      programs (
        ${PROGRAM_LIST_FIELDS},
        program_tags(
          tags(id, name, color, category)
        )
      )
    `)
    .eq('user_id', userId)
    .in('status', ['active', 'in_progress', 'completed'])
    // Prompt of the Day sessions anchor to a hidden program row; they are
    // not catalog enrollments and must never surface on Continue rails.
    .neq('program_id', DAILY_PROMPT_ANCHOR_PROGRAM_ID)
    .order('started_at', { ascending: false })
    // Fetch a broader history so completed programs can be labeled across the catalog.
    .limit(300);

  if (error) throw error;

  const enrollments = (data || []).map(session => {
    const program = session.programs as any;
    const promptCount = program?.prompt_count || 1;
    const currentIndex = session.current_prompt_index || 0;
    const status = session.status as 'active' | 'in_progress' | 'completed' | 'paused';
    const computedProgressPercent = Math.round((currentIndex / promptCount) * 100);
    const progressPercent = status === 'completed'
      ? 100
      : Math.max(0, Math.min(99, computedProgressPercent));

    return {
      id: session.id,
      program_id: session.program_id,
      user_id: session.user_id,
      status,
      progress_percent: progressPercent,
      current_prompt_index: currentIndex,
      started_at: session.started_at,
      completed_at: session.completed_at,
      program: {
        ...program,
        tags: program?.program_tags?.map((pt: any) => pt.tags).filter(Boolean) || [],
      },
    };
  });

  const creatorIds = [...new Set(enrollments.map((enrollment) => enrollment.program?.created_by).filter(Boolean))];
  let creatorByProfileId = new Map<string, { id: string; display_name: string | null; avatar_url: string | null; is_admin: boolean | null }>();
  if (creatorIds.length > 0) {
    const [{ data: profilesByProfileId }, { data: profilesByUserId }] = await Promise.all([
      supabase
        .from('member_profiles')
        .select('id, user_id, display_name, avatar_url, is_admin')
        .in('id', creatorIds),
      supabase
        .from('member_profiles')
        .select('id, user_id, display_name, avatar_url, is_admin')
        .in('user_id', creatorIds),
    ]);

    creatorByProfileId = new Map();
    [...(profilesByProfileId || []), ...(profilesByUserId || [])].forEach((profile) => {
      const normalized = {
        id: profile.user_id,
        display_name: profile.display_name ?? null,
        avatar_url: profile.avatar_url ?? null,
        is_admin: profile.is_admin ?? null,
      };
      creatorByProfileId.set(profile.id, normalized);
      creatorByProfileId.set(profile.user_id, normalized);
    });
  }

  const enrollmentsWithCreators = enrollments.map((enrollment) => ({
    ...enrollment,
    program: {
      ...enrollment.program,
      creator: enrollment.program?.created_by
        ? (creatorByProfileId.get(enrollment.program.created_by) ?? null)
        : null,
    },
  }));

  // De-duplicate by program_id, keeping only the most recent session per program
  // (sessions are already sorted by started_at desc, so first occurrence is most recent)
  const seenProgramIds = new Set<string>();
  return enrollmentsWithCreators.filter(enrollment => {
    if (seenProgramIds.has(enrollment.program_id)) {
      return false;
    }
    seenProgramIds.add(enrollment.program_id);
    return true;
  });
}

/**
 * Fetch a single program by ID
 * Fetches creator profile separately to avoid FK relationship issues
 * For course-type programs, fetches modules and units from their respective tables
 */
export async function fetchProgramById(programId: string): Promise<Program | null> {
  // Note: creator join removed - programs_created_by_fkey now points to auth.users, not profiles
  // TODO: Add proper creator lookup via separate query or database view
  const { data, error } = await supabase
    .from('programs')
    .select(`
      *,
      program_tags(
        tags(id, name, color, category)
      ),
      prompts!prompts_program_id_fkey(
        id,
        title,
        content,
        duration,
        order_index
      )
    `)
    .eq('id', programId)
    .is('deleted_at', null)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  // Fetch creator profile separately (no FK constraint exists)
  let creator: {
    display_name: string;
    avatar_url?: string | null;
    is_admin?: boolean | null;
  } | null = null;
  if (data.created_by) {
    const { data: profile } = await supabase
      .from('member_profiles')
      .select('display_name, avatar_url, is_admin')
      .eq('user_id', data.created_by)
      .maybeSingle();
    if (profile) {
      creator = profile;
    }
  }

  // Fetch approver profile if program is MHAB approved
  let approver: { display_name: string; avatar_url?: string | null } | null = null;
  if (data.mental_health_approved && data.mental_health_approved_by) {
    const { data: profile } = await supabase
      .from('member_profiles')
      .select('display_name, avatar_url')
      .eq('user_id', data.mental_health_approved_by)
      .maybeSingle();

    if (profile) {
      approver = profile;
    }
  }

  let modules: any[] = [];

  // For course-type programs, fetch real modules and units
  if (data.program_type === 'course') {
    const { data: modulesData } = await supabase
      .from('modules')
      .select('id, title, description, sort_order, is_active, program_id, created_at, updated_at')
      .eq('program_id', programId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (modulesData && modulesData.length > 0) {
      // Fetch units for all modules
      const moduleIds = modulesData.map(m => m.id);
      const { data: unitsData } = await supabase
        .from('units')
        .select('id, module_id, title, description, content, rich_content, sort_order, content_type, duration, attachment_urls, is_active, created_at, updated_at')
        .in('module_id', moduleIds)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      // Group units by module_id
      const unitsByModule: Record<string, any[]> = {};
      (unitsData || []).forEach(unit => {
        if (!unitsByModule[unit.module_id]) {
          unitsByModule[unit.module_id] = [];
        }
        unitsByModule[unit.module_id].push(unit);
      });

      // Attach units to modules
      modules = modulesData.map(mod => ({
        ...mod,
        units: unitsByModule[mod.id] || [],
      }));
    }
  } else {
    // For PowerPrompt programs, prompts are stored directly on the program (not through modules)
    // We create a synthetic "module" structure to maintain compatibility with the UI
    const directPrompts = (data.prompts as any[])?.sort((a: any, b: any) => a.order_index - b.order_index) || [];

    // Create synthetic modules structure from direct prompts
    // PowerPrompt programs use 3 phases: Warm Up, Writing, Reflection
    modules = directPrompts.length > 0 ? [{
      id: 'default-module',
      title: 'Prompts',
      description: null,
      sort_order: 0,
      is_active: true,
      prompts: directPrompts,
    }] : [];
  }

  return {
    ...data,
    tags: (data.program_tags as any[])?.map(pt => pt.tags).filter(Boolean) || [],
    modules,
    created_by: data.created_by,
    creator,
    approver,
  } as Program;
}

/**
 * Hook to fetch all programs
 */
export function usePrograms(options?: { enabled?: boolean } | string) {
  const enabled = typeof options === 'object' ? (options?.enabled ?? true) : true;
  return useQuery({
    queryKey: programKeys.all,
    queryFn: fetchPrograms,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  });
}

export function useFreePrograms(options?: { enabled?: boolean; samplesOnly?: boolean }) {
  const samplesOnly = options?.samplesOnly === true;
  return useQuery({
    queryKey: samplesOnly ? programKeys.freeSamples : programKeys.free,
    queryFn: samplesOnly ? fetchFreeSamplePrograms : fetchFreePrograms,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });
}

/**
 * Hook to fetch newest programs
 */
export function useNewestPrograms(limit = 6, options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const { data: discoverySignals, isLoading: discoverySignalsLoading } = useDiscoveryProfileSignals(user?.id);
  return useQuery({
    queryKey: programKeys.newest(limit, user?.id, discoverySignals?.birthday ?? null),
    queryFn: () => fetchNewestPrograms(limit, discoverySignals),
    enabled: (options?.enabled ?? true) && (!user?.id || !discoverySignalsLoading),
    staleTime: 5 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });
}

/**
 * Catalog PowerPrompts that take ten minutes or less, free programs first,
 * then newest by publish date. Bounded (over-fetch, discovery filter, cut),
 * so it never loads the full catalog.
 */
const QUICK_PROGRAM_MAX_MINUTES = 10;

export function sortQuickPrograms<T extends { access_tier?: string | null }>(programs: T[]): T[] {
  // Stable: the query already orders by published_at desc.
  return programs
    .map((program, index) => ({ program, index }))
    .sort((a, b) => {
      const aFree = a.program.access_tier === 'free' ? 0 : 1;
      const bFree = b.program.access_tier === 'free' ? 0 : 1;
      return aFree - bFree || a.index - b.index;
    })
    .map(({ program }) => program);
}

async function fetchQuickPrograms(limit = 12, discoverySignals?: DiscoveryProfileSignals): Promise<Program[]> {
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .lte('total_duration', QUICK_PROGRAM_MAX_MINUTES)
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(getDiscoveryCandidateLimit(limit))
  );

  if (error) throw error;

  const normalized = await normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));
  return sortQuickPrograms(filterProgramsForDiscovery(normalized, discoverySignals)).slice(0, limit);
}

export function useQuickPrograms(limit = 12, options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const { data: discoverySignals, isLoading: discoverySignalsLoading } = useDiscoveryProfileSignals(user?.id);
  return useQuery({
    queryKey: ['programs', 'quick', limit, user?.id, discoverySignals?.birthday ?? null],
    queryFn: () => fetchQuickPrograms(limit, discoverySignals),
    enabled: (options?.enabled ?? true) && (!user?.id || !discoverySignalsLoading),
    staleTime: 5 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });
}

/**
 * Hook to fetch user's enrolled programs
 */
export function useUserEnrollments(userId: string | undefined) {
  return useQuery({
    queryKey: ['enrollments', userId],
    queryFn: () => fetchUserEnrollments(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single program
 */
export function useProgram(
  programId: string | undefined,
  options?: { refetchOnWindowFocus?: boolean }
) {
  return useQuery({
    queryKey: programKeys.detail(programId),
    queryFn: () => fetchProgramById(programId!),
    enabled: !!programId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
  });
}

/**
 * Fetch popular programs by enrollment count
 * Uses a subquery to count user_sessions per program
 */
async function fetchPopularPrograms(limit = 8, discoverySignals?: DiscoveryProfileSignals): Promise<Program[]> {
  // First, get enrollment counts per program (recent window to keep it fast)
  const recentCutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
  const { data: enrollmentCounts, error: countError } = await supabase
    .from('user_sessions')
    .select('program_id')
    .not('program_id', 'is', null)
    .gte('started_at', recentCutoff)
    .order('started_at', { ascending: false })
    .limit(2000);

  if (countError) {
    // If RLS blocks counts, fall back to newest programs (RPC-backed).
    return fetchNewestPrograms(limit, discoverySignals);
  }

  // Count enrollments per program
  const programCounts = new Map<string, number>();
  (enrollmentCounts || []).forEach(session => {
    if (session.program_id) {
      programCounts.set(session.program_id, (programCounts.get(session.program_id) || 0) + 1);
    }
  });

  // Get top program IDs by enrollment count
  const topProgramIds = Array.from(programCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, getDiscoveryCandidateLimit(limit))
    .map(([id]) => id);

  if (topProgramIds.length === 0) {
    // Fallback to newest if no enrollments
    return fetchNewestPrograms(limit, discoverySignals);
  }

  // Fetch full program data for top programs (admin-created only)
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .in('id', topProgramIds)
  );

  if (error) {
    return fetchNewestPrograms(limit, discoverySignals);
  }

  // Sort by enrollment count (preserving popularity order)
  const programsWithEnrollment = (data || []).map(program => ({
    ...program,
    _enrollmentCount: programCounts.get(program.id) || 0,
  }));
  const programsWithTags = await normalizePrograms(programsWithEnrollment.filter(program => isOfficialCatalogProgram(program)));

  return filterProgramsForDiscovery(programsWithTags, discoverySignals)
    .sort((a, b) => (b._enrollmentCount || 0) - (a._enrollmentCount || 0))
    .slice(0, limit) as Program[];
}

/**
 * Fetch suggested programs based on user's tag preferences
 * Looks at tags from user's enrolled programs and finds similar ones
 */
async function fetchSuggestedPrograms(
  userId: string,
  limit = 8,
  discoverySignals?: DiscoveryProfileSignals
): Promise<Program[]> {
  // Get user's enrolled program IDs
  const { data: enrollments, error: enrollError } = await supabase
    .from('user_sessions')
    .select('program_id')
    .eq('user_id', userId);

  if (enrollError) throw enrollError;

  const enrolledProgramIds = new Set((enrollments || []).map(e => e.program_id).filter(Boolean));

  if (enrolledProgramIds.size === 0) {
    // No enrollments - return popular programs instead
    return fetchPopularPrograms(limit, discoverySignals);
  }

  // Get tags from user's enrolled programs
  const { data: enrolledPrograms, error: programError } = await supabase
    .from('programs')
    .select(`
      id,
      program_tags(
        tag_id
      )
    `)
    .in('id', Array.from(enrolledProgramIds));

  if (programError) throw programError;

  // Count tag frequency from enrolled programs
  const tagCounts = new Map<string, number>();
  (enrolledPrograms || []).forEach(program => {
    (program.program_tags as { tag_id: string }[] || []).forEach(pt => {
      if (pt.tag_id) {
        tagCounts.set(pt.tag_id, (tagCounts.get(pt.tag_id) || 0) + 1);
      }
    });
  });

  // Get top 5 tags
  const topTagIds = Array.from(tagCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id]) => id);

  if (topTagIds.length === 0) {
    // No tags found - return popular programs
    return fetchPopularPrograms(limit, discoverySignals);
  }

  // Find programs with these tags that user hasn't enrolled in
  const { data: suggestedProgramTags, error: suggestError } = await supabase
    .from('program_tags')
    .select('program_id')
    .in('tag_id', topTagIds);

  if (suggestError) throw suggestError;

  // Filter out enrolled programs and count matches
  const suggestionCounts = new Map<string, number>();
  (suggestedProgramTags || []).forEach(pt => {
    if (pt.program_id && !enrolledProgramIds.has(pt.program_id)) {
      suggestionCounts.set(pt.program_id, (suggestionCounts.get(pt.program_id) || 0) + 1);
    }
  });

  // Get top suggested program IDs
  const suggestedIds = Array.from(suggestionCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, getDiscoveryCandidateLimit(limit))
    .map(([id]) => id);

  if (suggestedIds.length === 0) {
    // No suggestions found - return popular programs
    return fetchPopularPrograms(limit, discoverySignals);
  }

  // Fetch full program data (admin-created only)
  const { data, error } = await executeProgramsSelect(programSelect =>
    supabase
      .from('programs')
      .select(programSelect)
      .eq('program_type', 'powerprompt')
      .eq('is_published', true)
      .eq('program_scope', 'catalog')
      .eq('creation_source', 'admin')
      .is('deleted_at', null)
      .in('id', suggestedIds)
  );

  if (error) throw error;

  const normalized = await normalizePrograms((data || []).filter(program => isOfficialCatalogProgram(program)));

  return filterProgramsForDiscovery(normalized, discoverySignals)
    .sort((a, b) => (suggestionCounts.get(b.id) || 0) - (suggestionCounts.get(a.id) || 0))
    .slice(0, limit);
}

/**
 * Hook to fetch popular programs (by enrollment count)
 */
export function usePopularPrograms(limit = 8, options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const { data: discoverySignals, isLoading: discoverySignalsLoading } = useDiscoveryProfileSignals(user?.id);
  return useQuery({
    queryKey: programKeys.popular(limit, user?.id, discoverySignals?.birthday ?? null),
    queryFn: () => fetchPopularPrograms(limit, discoverySignals),
    staleTime: 5 * 60 * 1000, // 5 minutes - popularity changes slowly
    enabled: (options?.enabled ?? true) && (!user?.id || !discoverySignalsLoading),
  });
}

/**
 * Hook to fetch suggested programs based on user preferences
 */
export function useSuggestedPrograms(userId: string | undefined, limit = 8, options?: { enabled?: boolean }) {
  const { data: discoverySignals, isLoading: discoverySignalsLoading } = useDiscoveryProfileSignals(userId);
  return useQuery({
    queryKey: programKeys.suggested(userId, limit, discoverySignals?.birthday ?? null),
    queryFn: () => fetchSuggestedPrograms(userId!, limit, discoverySignals),
    enabled: !!userId && (options?.enabled ?? true) && !discoverySignalsLoading,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch PowerPrompt programs created by a specific user
 * Owners can manage all their programs. Profile listings only request public,
 * published programs and use a separate cache from the owner's drafts.
 */
export function useUserCreatedPrograms(userId: string | undefined, options: { publicOnly?: boolean } = {}) {
  const { user, loading } = useAuth();
  const publicOnly = options.publicOnly || !user?.id || user.id !== userId || Boolean(user.is_anonymous);

  return useQuery({
    queryKey: ['userCreatedPrograms', userId, publicOnly ? 'public' : 'owner', user?.id ?? null],
    queryFn: async () => {
      const { data, error } = await executeProgramsSelect(programSelect => {
        let query = supabase
          .from('programs')
          .select(programSelect)
          .eq('program_type', 'powerprompt')
          .eq('created_by', userId!)
          .eq('creation_source', 'frontend') // Only show user-created programs, not admin-created
          .is('deleted_at', null);

        if (publicOnly) {
          query = query.eq('is_published', true).eq('visibility', 'public').eq('program_scope', 'catalog');
        }

        return query.order('created_at', { ascending: false });
      });

      if (error) throw error;

      return normalizePrograms(data || []);
    },
    enabled: !!userId && !loading,
    staleTime: 30 * 1000,
  });
}
